import paymentTypes from './paymentTypes';
import { getCardTypeId } from './cardTypes';
import clientInputError from '../errors/clientInputError';
import { nullSafeToString } from '../lib/util';

// The API stores a display-only masked number; only the last two digits are ever known client side.
const MASKED_CARD_PREFIX = 'XXXX-XXXX-XXXX-XX';

const MIN_PAN_LENGTH = 13;
const MAX_PAN_LENGTH = 19;

const PAYMENT_TYPE_VALUES = Object.keys(paymentTypes).map(key => paymentTypes[key]);

/*
 * A raw card number must never reach this SDK. Accepting one would pull the consumer's page into a far
 * wider PCI scope than hosted fields require, and Collaterate has no use for it - the API only ever
 * wants a Braintree token. This catches the obvious mistake of passing the card number where the nonce
 * belongs. Real nonces and vault tokens contain non-digits, so digit-only values are the signal.
 */

function looksLikeCardNumber (value) {
    const text = nullSafeToString(value).trim();

    if (!/^[\d\s-]+$/u.test(text)) {
        return false;
    }

    const digits = text.replace(/[^\d]/gu, '');

    return digits.length >= MIN_PAN_LENGTH && digits.length <= MAX_PAN_LENGTH;
}

function validate (options) {
    const errors = [];
    const { type, paymentMethodNonce, savedPaymentMethodToken } = options;

    if (!PAYMENT_TYPE_VALUES.includes(type)) {
        errors.push({
            field: 'type',
            message: `Unknown payment type "${type}". Expected one of ${PAYMENT_TYPE_VALUES.join(', ')}.`,
        });

        // Nothing below can be judged without a known type.
        throw clientInputError(errors);
    }

    const requiresCard = type !== paymentTypes.TERMS;

    if (requiresCard) {
        const hasNonce = Boolean(nullSafeToString(paymentMethodNonce).trim());
        const hasToken = Boolean(nullSafeToString(savedPaymentMethodToken).trim());

        // The API rejects both-or-neither (ProjectV2Provider#validatePaymentTokens). Caught here so the
        // consumer gets a usable error instead of a 400.
        if (!hasNonce && !hasToken) {
            errors.push({
                field: 'paymentMethodNonce',
                message: 'A paymentMethodNonce or a savedPaymentMethodToken is required for this payment type.',
            });
        }

        if (hasNonce && hasToken) {
            errors.push({
                field: 'paymentMethodNonce',
                message: 'Provide either a paymentMethodNonce or a savedPaymentMethodToken, not both.',
            });
        }

        if (looksLikeCardNumber(paymentMethodNonce)) {
            errors.push({
                field: 'paymentMethodNonce',
                message: 'paymentMethodNonce looks like a card number. Tokenize the card with Braintree and pass the resulting nonce; the SDK never accepts raw card details.',
            });
        }

        /*
         * Enrolling a new PayPal account needs a per-customer Braintree client token, which only the
         * legacy checkout page can mint. Paying with an already vaulted PayPal account works, and that
         * always arrives as a vault token.
         */
        if (type === paymentTypes.PAYPAL && hasNonce) {
            errors.push({
                field: 'type',
                message: 'PayPal is supported only with a savedPaymentMethodToken from a vaulted PayPal account. Enrolling a new PayPal account is not supported.',
            });
        }
    }

    if (errors.length) {
        throw clientInputError(errors);
    }

    return requiresCard;
}

/**
 * A validated payment selection for a checkout. Create one with
 * {@link Collaterate.Checkout.setPayment Collaterate.Checkout.setPayment} rather than directly.
 *
 * @property {string} type - (Read only) The payment type: "CREDIT_CARD", "PAYPAL" or "TERMS".
 * @property {string} po - (Read only) The purchase order number, for terms payments.
 * @property {string} paymentMethodNonce - (Read only) A single-use Braintree nonce for a newly entered card.
 * @property {string} savedPaymentMethodToken - (Read only) A Braintree vault token for a saved card or PayPal account.
 * @property {string} cardHolder - (Read only) The name on the card.
 * @property {string} braintreeCardType - (Read only) The card type as Braintree reported it, e.g. "Visa".
 * @property {string} lastTwo - (Read only) The last two digits of the card, used to build the stored masked number.
 * @property {string} deviceData - (Read only) The Braintree data collector payload, used for fraud scoring.
 * @property {boolean} requiresCard - (Read only) Whether this payment needs a payment token stored on the project.
 * @property {boolean} usesSavedPaymentMethod - (Read only) Whether this payment uses a vaulted payment method.
 */

class CheckoutPayment {
    /**
     * Build the `ProjectCcPaymentInfoDTO` payload for `PUT /projects/v2/{projectId}/creditCard`.
     *
     * For a vaulted payment method only the token is sent: the server reads the cardholder, card type
     * and masked number back from Braintree, so sending client-supplied values would at best be
     * ignored and at worst disagree with what is actually charged.
     *
     * @param {CheckoutPayment} payment - The payment to serialize.
     * @returns {?Object} The request payload, or null for payment types that store no card.
     */

    static toDB (payment) {
        if (!payment.requiresCard) {
            return null;
        }

        if (payment.usesSavedPaymentMethod) {
            return {
                savedPaymentMethodToken: payment.savedPaymentMethodToken,
                deviceData: payment.deviceData,
            };
        }

        return {
            cardHolder: payment.cardHolder,
            cardType: getCardTypeId(payment.braintreeCardType),
            cardNumber: `${MASKED_CARD_PREFIX}${payment.lastTwo}`,
            deviceData: payment.deviceData,
            paymentMethodNonce: payment.paymentMethodNonce,
        };
    }

    constructor (options = {}) {
        const requiresCard = validate(options);

        this.type = options.type;
        this.po = nullSafeToString(options.po);
        this.paymentMethodNonce = nullSafeToString(options.paymentMethodNonce);
        this.savedPaymentMethodToken = nullSafeToString(options.savedPaymentMethodToken);
        this.cardHolder = nullSafeToString(options.cardHolder);
        this.braintreeCardType = nullSafeToString(options.braintreeCardType);
        this.lastTwo = nullSafeToString(options.lastTwo);
        this.deviceData = nullSafeToString(options.deviceData);
        this.requiresCard = requiresCard;
        this.usesSavedPaymentMethod = Boolean(this.savedPaymentMethodToken);

        /*
         * Braintree's fraud tools depend on device data, and the checkout this replaces always sent it
         * because the Angular directive always created a data collector. Nothing server side enforces
         * it, so a consumer who skips braintree.dataCollector silently loses that protection.
         */
        if (requiresCard && !this.usesSavedPaymentMethod && !this.deviceData) {
            console.warn('Collaterate.Checkout: no deviceData was supplied with this card. Create a braintree.dataCollector and pass its deviceData to keep Braintree fraud protection effective.');
        }

        Object.freeze(this);
    }
}

export default CheckoutPayment;
